import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useLeaderboard } from '../hooks/useLeaderboard';
import { useAuthContext } from '../context/AuthContext';
import { useMyBouts } from '../hooks/useMyBouts';
import { usePublicLobby } from '../hooks/usePublicLobby';
import { joinBout, deleteBout, declineBout } from '../services/boutService';
import { BADGES } from '../utils/constants';
import LoadingSpinner from '../components/LoadingSpinner';
import CreateBoutForm from '../components/CreateBoutForm';
import BoutCard from '../components/BoutCard';
import './ArenaPage.css';

export default function ArenaPage() {
  const { entries, loading: leaderboardLoading } = useLeaderboard();
  const { user, userProfile, isAuthenticated } = useAuthContext();
  const { myBouts, openBouts, boutInvites } = useMyBouts();
  const { lobbyBouts, loading: lobbyLoading } = usePublicLobby();
  const [showCreateBout, setShowCreateBout] = useState(false);
  const [actionLoading, setActionLoading] = useState({});

  const activeBouts = myBouts.filter((b) => b.status === 'active');
  const completedBouts = myBouts.filter((b) => b.status === 'completed');

  const handleJoinBout = async (boutId) => {
    setActionLoading((prev) => ({ ...prev, [boutId]: true }));
    try {
      await joinBout(boutId);
      toast.success('Joined the bout!');
    } catch (err) {
      toast.error(err?.message || 'Failed to join bout');
    } finally {
      setActionLoading((prev) => ({ ...prev, [boutId]: false }));
    }
  };

  const handleDeleteBout = async (boutId) => {
    setActionLoading((prev) => ({ ...prev, [boutId]: true }));
    try {
      await deleteBout(boutId);
      toast.success('Bout deleted');
    } catch (err) {
      toast.error(err?.message || 'Failed to delete bout');
    } finally {
      setActionLoading((prev) => ({ ...prev, [boutId]: false }));
    }
  };

  const handleAcceptBout = async (boutId) => {
    setActionLoading((prev) => ({ ...prev, [boutId]: true }));
    try {
      await joinBout(boutId);
      toast.success('Bout accepted! Game on!');
    } catch (err) {
      toast.error(err?.message || 'Failed to accept bout');
    } finally {
      setActionLoading((prev) => ({ ...prev, [boutId]: false }));
    }
  };

  const handleDeclineBout = async (boutId) => {
    setActionLoading((prev) => ({ ...prev, [boutId]: true }));
    try {
      await declineBout(boutId);
      toast.success('Bout declined');
    } catch (err) {
      toast.error(err?.message || 'Failed to decline bout');
    } finally {
      setActionLoading((prev) => ({ ...prev, [boutId]: false }));
    }
  };

  return (
    <div className="arena-page">
      <div className="arena-header">
        <div>
          <h1>Arena</h1>
          <p className="arena-subtitle">Compete in the global contest or challenge friends</p>
        </div>
        {isAuthenticated && (
          <button
            className="btn-create-bout"
            onClick={() => setShowCreateBout(true)}
          >
            + Create Bout
          </button>
        )}
      </div>

      {showCreateBout && (
        <CreateBoutForm onClose={() => setShowCreateBout(false)} />
      )}

      {/* Bout Invitations */}
      {isAuthenticated && boutInvites.length > 0 && (
        <div className="arena-section">
          <div className="arena-section-header">
            <h2>Bout Invitations</h2>
            <span className="arena-section-count">{boutInvites.length}</span>
          </div>
          {boutInvites.map((bout) => (
            <BoutCard
              key={bout.id}
              bout={bout}
              currentUid={user?.uid}
              onAccept={handleAcceptBout}
              onDecline={handleDeclineBout}
              actionLoading={actionLoading[bout.id]}
            />
          ))}
        </div>
      )}

      {/* My Active Bouts */}
      {isAuthenticated && activeBouts.length > 0 && (
        <div className="arena-section">
          <div className="arena-section-header">
            <h2>My Bouts</h2>
            <span className="arena-section-count">{activeBouts.length}</span>
          </div>
          {activeBouts.map((bout) => (
            <BoutCard
              key={bout.id}
              bout={bout}
              currentUid={user?.uid}
              actionLoading={actionLoading[bout.id]}
            />
          ))}
        </div>
      )}

      {/* Open Bouts (unfilled, created by me) */}
      {isAuthenticated && openBouts.length > 0 && (
        <div className="arena-section">
          <div className="arena-section-header">
            <h2>Open Bouts</h2>
            <span className="arena-section-count">{openBouts.length}</span>
          </div>
          {openBouts.map((bout) => (
            <BoutCard
              key={bout.id}
              bout={bout}
              currentUid={user?.uid}
              onDelete={handleDeleteBout}
              actionLoading={actionLoading[bout.id]}
            />
          ))}
        </div>
      )}

      {/* Public Lobby */}
      {isAuthenticated && (
        <div className="arena-section">
          <div className="arena-section-header">
            <h2>Public Lobby</h2>
          </div>
          {lobbyLoading ? (
            <div className="arena-empty">Loading lobby...</div>
          ) : lobbyBouts.length === 0 ? (
            <div className="arena-empty">No open bouts in the lobby right now.</div>
          ) : (
            lobbyBouts.map((bout) => (
              <BoutCard
                key={bout.id}
                bout={bout}
                currentUid={user?.uid}
                onJoin={handleJoinBout}
                actionLoading={actionLoading[bout.id]}
              />
            ))
          )}
        </div>
      )}

      {/* Global Contest (existing leaderboard) */}
      <div className="arena-section">
        <div className="arena-section-header">
          <h2>Global Contest</h2>
        </div>
        <div className="leaderboard-card">
          {leaderboardLoading ? (
            <LoadingSpinner message="Loading standings..." />
          ) : entries.length === 0 ? (
            <div className="leaderboard-empty">
              <p>No streaks yet. Be the first to get on the board!</p>
            </div>
          ) : (
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th>Player</th>
                  <th>Active Pick</th>
                  <th>Current</th>
                  <th>Long</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => {
                  const badge = entry.uid === user?.uid
                    ? (userProfile?.activeBadge || entry.activeBadge)
                    : entry.activeBadge;

                  let activePickDisplay;
                  if (!entry.activePick) {
                    activePickDisplay = (
                      <span className="pick-status pick-none">No Pick</span>
                    );
                  } else if (entry.activePick.status === 'pending') {
                    activePickDisplay = (
                      <span className="pick-status pick-pending">Pick Selected</span>
                    );
                  } else {
                    activePickDisplay = (
                      <span className="pick-status pick-locked">
                        {entry.activePick.pickTeamLogo && (
                          <img
                            src={entry.activePick.pickTeamLogo}
                            alt=""
                            className="pick-team-logo"
                          />
                        )}
                        {entry.activePick.pickTeamAbbr || entry.activePick.pickTeamName}
                      </span>
                    );
                  }

                  return (
                    <tr key={entry.uid}>
                      <td className="player">
                        <span className="player-badge">
                          {BADGES[badge]?.emoji || '🐣'}
                        </span>
                        <Link to={`/users/${entry.username}`} className="player-username-link">
                          {entry.username}
                        </Link>
                      </td>
                      <td className="active-pick">{activePickDisplay}</td>
                      <td className="streak">🔥 {entry.currentStreak}</td>
                      <td className="best">{entry.longestStreak}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Completed Bouts */}
      {isAuthenticated && completedBouts.length > 0 && (
        <div className="arena-section">
          <div className="arena-section-header">
            <h2>Completed Bouts</h2>
            <span className="arena-section-count">{completedBouts.length}</span>
          </div>
          {completedBouts.map((bout) => (
            <BoutCard
              key={bout.id}
              bout={bout}
              currentUid={user?.uid}
              actionLoading={actionLoading[bout.id]}
            />
          ))}
        </div>
      )}
    </div>
  );
}
